/**
 * UnifiedDetailPage - Combined content detail page for all content types
 * 
 * Handles: movie, series, program content types
 * 
 * Features:
 * - Shared DetailHeroScene
 * - Movie: ticketing, subscription, QR popover
 * - Series/Program: Episode selector
 * - Common: Artist slider, related content, description
 */
import {
    Box,
    Button,
    Typography,
    Collapse,
    Popover,
    CircularProgress,
} from "@mui/material";
import { FOCUS_BORDER } from "../../config/focusStyles";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import PendingIcon from "@mui/icons-material/Pending";
import {
    FocusContext,
    useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import QRCode from "react-qr-code";

// Common components
import DetailHeroScene from "../common/DetailHeroScene";
import ContentAction from "../common/contentAction";
import ArtistSlider from "../common/ArtistSlider";

// Series-specific
import SeriesEpisodes from "../series/seriesEpisodes";
import SeriesRelatedMovies from "../series/relatedMovies";

// Movie-specific
import MovieRelatedMovies from "../movie/relatedMovies";

// Services & Utils
import httpService from "../../services/http.service";
import { getBillingApiRoute, getCMSApiRoute } from "../../utils/api.utils";
import { getContentTypeName, getUserSubscriptionStatus } from "../../utils/data.utils";
import smoothScroll from "../../utils/animateScroll";
import { preloadPlayerData } from "../../utils/hooks/usePlayerDataCache";
import { setAutoResumeCookie } from "../../utils/autoResumeCookie";
import { useInputMode } from "../../utils/hooks/useInputMode";

const ScrollingRows = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  height: 100vh;
  width: 100%;
  position: relative;
`;

const RelatedContentSection = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  width: 100%;
  padding-right: 5%;
  margin-top: 1rem;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
`;

const RelatedContentTitle = styled.h3`
  color: #D9D9D9;
  font-weight: 700;
  font-size: 28px;
  margin: 0;
  font-family: 'Anjoman Max FN', sans-serif;
`;

// ============================================================
// Countdown & Auto-Redirect Components
// ============================================================

const CountdownOuterWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 72px; /* same as play/action buttons */
  margin-left: 12px; /* exact same spacing as other action buttons */
`;

const CountdownLabel = styled.div`
  position: absolute;
  bottom: calc(100% + 12px); /* float gracefully above the box */
  right: 0; /* align with the right edge (Farsi RTL) */
  display: flex;
  align-items: center;
  gap: 8px;
  color: #E2E8F0;
  font-size: 20px;
  font-weight: 600;
  font-family: 'Anjoman Max FN', sans-serif;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6); /* slight shadow as requested */
  white-space: nowrap;
`;

const CountdownContainer = styled.div`
  display: flex;
  align-items: center;
  background: rgba(180, 30, 30, 0.75); /* same background as standard action buttons */
  border: 2px solid rgba(255, 255, 255, 0.25); /* same border as standard action buttons */
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4); /* same box-shadow as standard action buttons */
  border-radius: 15px; /* same as play button */
  height: 72px; /* same height as action buttons */
  padding: 0 24px;
  box-sizing: border-box;
  animation: fadeInDetail 0.5s ease-out;

  @keyframes fadeInDetail {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const LivePulseDot = styled.span`
  width: 10px;
  height: 10px;
  background-color: #EF4444;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 0 8px #EF4444;
  animation: pulseLive 1.5s infinite;

  @keyframes pulseLive {
    0% {
      transform: scale(0.9);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
    }
    70% {
      transform: scale(1.1);
      box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
    }
    100% {
      transform: scale(0.9);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
    }
  }
`;

const CountdownGrid = styled.div`
  display: flex;
  flex-direction: row-reverse;
  gap: 12px;
  align-items: center;
  height: 100%;
`;

const TimeSegment = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 100%;
`;

const TimeValue = styled.div`
  color: #FFFFFF;
  font-size: 22px;
  font-weight: 700;
  font-family: 'Anjoman Max FN', sans-serif;
  text-shadow: 0 2px 4px rgba(0,0,0,0.5);
  line-height: 22px;
`;

const TimeLabel = styled.span`
  color: #94A3B8;
  font-size: 11px;
  font-weight: 500;
  font-family: 'Anjoman Max FN', sans-serif;
  line-height: 11px;
  margin-top: 2px;
`;

const RedirectNotification = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  background: rgba(239, 68, 68, 0.15);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(239, 68, 68, 0.3);
  box-shadow: 0 4px 20px rgba(239, 68, 68, 0.1);
  border-radius: 15px;
  padding: 12px 20px;
  height: 72px;
  box-sizing: border-box;
  animation: slideInNotification 0.3s ease-out;

  @keyframes slideInNotification {
    from { opacity: 0; transform: translateX(20px); }
    to { opacity: 1; transform: translateX(0); }
  }
`;

const RedirectText = styled.div`
  color: #FEE2E2;
  font-size: 18px;
  font-weight: 500;
  font-family: 'Anjoman Max FN', sans-serif;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RedirectTimerBadge = styled.span`
  background: #EF4444;
  color: #FFFFFF;
  border-radius: 8px;
  padding: 2px 8px;
  font-weight: 700;
  font-size: 18px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 24px;
  height: 24px;
`;

const CancelRedirectButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #FFFFFF;
  border-radius: 10px;
  padding: 6px 16px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Anjoman Max FN', sans-serif;
  transition: all 0.2s ease;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

const CancelButton = ({ onEnterPress, onFocus }) => {
    const { ref, focused, focusSelf } = useFocusable({
        onFocus,
        onEnterPress,
    });
    const { setMouseFocus } = useInputMode();
    const handleMouseEnter = useCallback(() => {
        setMouseFocus(true);
        focusSelf();
    }, [focusSelf, setMouseFocus]);

    return (
        <CancelRedirectButton
            ref={ref}
            onClick={onEnterPress}
            onMouseEnter={handleMouseEnter}
            style={{
                background: focused ? "#FFFFFF" : "rgba(255, 255, 255, 0.1)",
                color: focused ? "#EF4444" : "#FFFFFF",
                border: focused ? "2px solid #FFFFFF" : "1px solid rgba(255, 255, 255, 0.2)",
            }}
        >
            لغو
        </CancelRedirectButton>
    );
};

// ============================================================
// Shared Button Components
// ============================================================


const PlayButton = ({ onEnterPress, label, onFocus, loading, autofocus = false }) => {
    const { ref, focused, focusSelf } = useFocusable({
        onEnterPress,
        onFocus,
    });
    const { setMouseFocus } = useInputMode();
    const handleMouseEnter = useCallback(() => {
        setMouseFocus(true);
        focusSelf();
    }, [focusSelf, setMouseFocus]);

    useEffect(() => {
        if (autofocus) focusSelf();
    }, [autofocus, focusSelf]);

    return (
        <Button
            ref={ref}
            onClick={onEnterPress}
            onMouseEnter={handleMouseEnter}
            className="cursor-pointer"
            sx={{
                minWidth: "fit-content",
                height: "72px",
                backgroundColor: focused ? "rgba(255,255,255,0.95)" : "rgba(180, 30, 30, 0.75)",
                fontSize: "32px",
                fontWeight: 600,
                lineHeight: "32px",
                color: focused ? "#D32F2F" : "#F0F0F0",
                borderRadius: "15px",
                padding: "24px",
                border: focused ? "2px solid #FFF" : "2px solid rgba(255,255,255,0.25)",
                boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
                textShadow: focused ? "none" : "0 1px 2px rgba(0,0,0,0.8)",
                marginLeft: "12px",
                transition: "all 0.15s ease",
                "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.95)",
                    color: "#D32F2F",
                    border: "2px solid #FFF",
                    textShadow: "none",
                },
            }}
            disabled={loading}
        >
            <div className="flex justify-center items-center">
                {loading ? (
                    <CircularProgress size={32} sx={{ color: focused ? "#D32F2F" : "#FFF", opacity: 0.8 }} />
                ) : (
                    <React.Fragment>
                        <PlayCircleIcon sx={{ marginLeft: "6px", fontSize: "32px" }} />
                        <Typography fontSize={30} fontWeight={600}>
                            {label || "تماشا کن"}
                        </Typography>
                    </React.Fragment>
                )}
            </div>
        </Button>
    );
};

const PlayButtonWithPopover = ({ label, onFocus, children }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [open, setIsOpen] = useState(Boolean(anchorEl));

    const handleClick = (event) => {
        setAnchorEl(open ? null : event.currentTarget);
        setIsOpen(true);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setIsOpen(false);
    };

    const { ref, focused, focusSelf } = useFocusable({
        onFocus: (props) => {
            onFocus(props);
            setAnchorEl(ref.current);
            setIsOpen(true);
        },
        onBlur: () => {
            setAnchorEl(null);
            setIsOpen(false);
        },
    });
    const { setMouseFocus } = useInputMode();
    const handleMouseEnterPopover = useCallback(() => {
        setMouseFocus(true);
        focusSelf();
    }, [focusSelf, setMouseFocus]);

    useEffect(() => {
        focusSelf();
    }, [focusSelf]);

    useEffect(() => {
        setAnchorEl(ref.current);
        setIsOpen(focused);
    }, [focused]);

    return (
        <React.Fragment>
            <Button
                onClick={handleClick}
                onMouseEnter={handleMouseEnterPopover}
                ref={ref}
                sx={{
                    minWidth: "fit-content",
                    backgroundColor: "#6A0000",
                    height: '72px',
                    fontSize: "32px",
                    fontWeight: 500,
                    lineHeight: "32px",
                    color: "#F0F0F0",
                    ml: 2,
                    borderRadius: "24px",
                    padding: "15px 32px",
                    border: open ? FOCUS_BORDER.active : FOCUS_BORDER.inactive,
                    "&:hover": {
                        backgroundColor: "rgb(80,0,0)",
                    },
                }}
            >
                <PlayCircleIcon sx={{ marginLeft: "5%" }} />
                <Typography minWidth={"fit-content"} fontSize={25} fontWeight={700}>
                    {label || "تماشا کن"}
                </Typography>
            </Button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "center",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "center",
                    horizontal: "right",
                }}
                slotProps={{
                    paper: {
                        style: {
                            padding: 20,
                            maxWidth: 400,
                            background: "#121212",
                            borderRadius: "24px",
                            color: "whitesmoke",
                        },
                    },
                }}
            >
                <Box>{children}</Box>
            </Popover>
        </React.Fragment>
    );
};

const PreviewButton = ({ onEnterPress, onFocus }) => {
    const { ref, focused, focusSelf } = useFocusable({
        onFocus,
        onEnterPress,
    });
    const { setMouseFocus } = useInputMode();
    const handleMouseEnter = useCallback(() => {
        setMouseFocus(true);
        focusSelf();
    }, [focusSelf, setMouseFocus]);

    return (
        <Button
            ref={ref}
            onClick={onEnterPress}
            onMouseEnter={handleMouseEnter}
            className="cursor-pointer"
            sx={{
                width: "fit-content",
                height: "72px",
                background: focused ? "rgba(255,255,255,0.95)" : "rgba(180, 30, 30, 0.75)",
                fontSize: "32px",
                fontWeight: 600,
                color: focused ? "#D32F2F" : "#F0F0F0",
                borderRadius: "15px",
                padding: "24px",
                border: focused ? "2px solid #FFF" : "2px solid rgba(255,255,255,0.25)",
                boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
                textShadow: focused ? "none" : "0 1px 2px rgba(0,0,0,0.8)",
                marginLeft: "12px",
                transition: "all 0.15s ease",
                "&:hover": {
                    background: "rgba(255,255,255,0.95)",
                    color: "#D32F2F",
                    border: "2px solid #FFF",
                    textShadow: "none",
                },
            }}
        >
            <Typography fontSize={32} fontWeight={600}>
                پیش‌نمایش
            </Typography>
        </Button>
    );
};

// ============================================================
// Main Component
// ============================================================

const UnifiedDetailPage = ({ content = {} }) => {
    const navigate = useNavigate();
    const isLogin = localStorage.getItem("webTvUserToken");
    const { ref, focusKey } = useFocusable();

    // Content type detection
    const contentKind = content?.contentType?.kind || "movie";
    const isSeries = contentKind === "series" || contentKind === "program";
    // isMovie now includes ALL non-series content (movie, music, etc.)
    const isMovie = !isSeries;

    // Movie-specific state
    const [isPurchased, setIsPurchased] = useState(false);
    const [cinemaTicketSlug, setCinemaTicketSlug] = useState("");
    const [canPlay, setCanPlay] = useState();
    const [canPlayLoading, setCanPlayLoading] = useState(true);

    // Countdown states for livestreams
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [hadActiveCountdown, setHadActiveCountdown] = useState(false);
    const [autoRedirectCounter, setAutoRedirectCounter] = useState(null);

    const handleWatchEventRef = React.useRef(null);
    useEffect(() => {
        handleWatchEventRef.current = handleWatchEvent;
    });

    useEffect(() => {
        if (!content?.isLive || !content?.liveInfo?.liveStartDate) {
            setHadActiveCountdown(false);
            setTimeRemaining(null);
            return;
        }

        const liveStartDate = content.liveInfo.liveStartDate;
        const targetTime = typeof liveStartDate === 'number' ? liveStartDate : new Date(liveStartDate).getTime();
        
        const updateCountdown = () => {
            const now = new Date().getTime();
            const diff = targetTime - now;
            if (diff <= 0) {
                setTimeRemaining(0);
                return true;
            } else {
                setTimeRemaining(diff);
                return false;
            }
        };

        const isFinished = updateCountdown();
        if (isFinished) {
            setHadActiveCountdown(false);
            return;
        }

        setHadActiveCountdown(true);

        const interval = setInterval(() => {
            const finished = updateCountdown();
            if (finished) {
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [content]);

    useEffect(() => {
        if (timeRemaining === 0 && hadActiveCountdown) {
            setAutoRedirectCounter(5);

            const interval = setInterval(() => {
                setAutoRedirectCounter((prev) => {
                    if (prev === null) return null;
                    if (prev <= 1) {
                        clearInterval(interval);
                        if (handleWatchEventRef.current) {
                            handleWatchEventRef.current();
                        }
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(interval);
        } else {
            setAutoRedirectCounter(null);
        }
    }, [timeRemaining, hadActiveCountdown]);

    // Input mode for scroll behavior
    const { isMouseFocus, isMouseFocusRef } = useInputMode();

    // Scroll handler
    const onRowFocus = useCallback(
        (props) => {
            // Skip scroll adjustment when focus originated from mouse hover
            if (isMouseFocusRef.current) return;
            const { y, height } = props;
            const targetScroll = y > height - 200 ? y - 200 : 0;
            smoothScroll(ref, targetScroll, 500, isMouseFocus);
        },
        [ref, isMouseFocus]
    );

    // ============================================================
    // Movie-specific logic
    // ============================================================

    const getCinemaTicketData = async () => {
        try {
            const { slug } = content;
            const response = await httpService.get(getCMSApiRoute("cinema-ticket"), {
                params: {
                    pageIndex: 1,
                    pageNumber: 1,
                    contentKey: decodeURIComponent(slug),
                },
            });

            if (response.status === 200) {
                const { data } = response.data;
                setCinemaTicketSlug(data?.results[0]?.slug);
                await isCinemaTicketPurchased(data.results[0]?.slug, slug);
            }
        } catch (error) {
            console.log(`Error on CinemaTicket Get: ${error}`);
        }
    };

    const isCinemaTicketPurchased = async (slug, contentSlug) => {
        try {
            const response = await httpService.get(getBillingApiRoute("payment"), {
                params: {
                    pageNumber: 1,
                    pageIndex: 40,
                    orderType: "cinema_ticket",
                    status: 1,
                    ticketKey: slug,
                },
            });

            if (response.data?.data?.results.length !== 0) {
                setIsPurchased(true);
            }
        } catch (error) {
            console.log(`Error on CinemaTicket purchase check: ${error}`);
        }
    };

    const reportContentWatch = async () => {
        try {
            await httpService.post(getCMSApiRoute("action/watch"), {
                contentKey: content.slug,
            });
        } catch (error) {
            console.log(error);
        }
    };

    const getPlayUrl = () => {
        return "/watch/" + content.slug;
    };

    const handleWatchEvent = async () => {
        // Check if user has watch history for this content (re-entry scenario)
        // If so, set autoResume cookie before navigating to player (skip for live streams)
        const isLiveStream = content?.isLive === 1 || content?.isLive === true;
        if (!isLiveStream && (content.watchInfo?.elapsedTime > 0 || content.options?.elapsedTime > 0)) {
            setAutoResumeCookie({
                slug: content.slug,
                season: null,
                episode: null,
            });
            console.log('[AutoResume] Cookie set for re-entry:', content.slug);
        }

        try {
            if (content.isTicketing === 1) {
                if (isPurchased === false) {
                    if (!isLogin) {
                        navigate(`/login?ref=${encodeURIComponent(window.location.pathname)}`);
                    }
                } else {
                    if (!isLogin) {
                        navigate(`/login?ref=${encodeURIComponent(`/watch/onlinebakhish/${content.slug}/${cinemaTicketSlug}/1`)}`);
                    } else {
                        navigate(`/watch/onlinebakhish/${content.slug}/${cinemaTicketSlug}/1`);
                    }
                }
            } else {
                if (content.isFree === 1) {
                    if (!isLogin) {
                        navigate(`/login?ref=${encodeURIComponent(getPlayUrl())}`);
                    } else {
                        await reportContentWatch();
                        navigate(getPlayUrl());
                    }
                } else {
                    if (!isLogin) {
                        navigate(`/login?ref=${encodeURIComponent(window.location.pathname)}`);
                    } else {
                        if (!canPlay) {
                            navigate("/subscription");
                        } else {
                            await reportContentWatch();
                            navigate(getPlayUrl());
                        }
                    }
                }
            }
        } catch (error) {
            console.log(error);
        }
    };

    const getPlayLabel = () => {
        if (!isLogin) {
            if (content.isTicketing !== 1 || isPurchased === true) {
                return "ورود و تماشا";
            } else {
                return "ورود و خرید بلیط";
            }
        } else {
            if (content.isTicketing !== 1 || isPurchased === true) {
                if (content.isFree === 1 || canPlay) {
                    return "تماشا کن";
                } else {
                    return "خرید اشتراک";
                }
            } else {
                return "خرید بلیط";
            }
        }
    };

    const getCountdownComponent = () => {
        if (!timeRemaining) return null;

        const diff = timeRemaining;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((diff / 1000 / 60) % 60);
        const seconds = Math.floor((diff / 1000) % 60);

        return (
            <CountdownOuterWrapper>
                <CountdownLabel>
                    <LivePulseDot />
                    <span>شروع پخش زنده در:</span>
                </CountdownLabel>
                <CountdownContainer>
                    <CountdownGrid>
                        {days > 0 && (
                            <React.Fragment>
                                <TimeSegment>
                                    <TimeValue>{days}</TimeValue>
                                    <TimeLabel>روز</TimeLabel>
                                </TimeSegment>
                                <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '20px', alignSelf: 'center', marginBottom: '8px' }}>:</span>
                            </React.Fragment>
                        )}
                        <TimeSegment>
                            <TimeValue>{String(hours).padStart(2, '0')}</TimeValue>
                            <TimeLabel>ساعت</TimeLabel>
                        </TimeSegment>
                        <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '20px', alignSelf: 'center', marginBottom: '8px' }}>:</span>
                        <TimeSegment>
                            <TimeValue>{String(minutes).padStart(2, '0')}</TimeValue>
                            <TimeLabel>دقیقه</TimeLabel>
                        </TimeSegment>
                        <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '20px', alignSelf: 'center', marginBottom: '8px' }}>:</span>
                        <TimeSegment>
                            <TimeValue>{String(seconds).padStart(2, '0')}</TimeValue>
                            <TimeLabel>ثانیه</TimeLabel>
                        </TimeSegment>
                    </CountdownGrid>
                </CountdownContainer>
            </CountdownOuterWrapper>
        );
    };

    const renderRedirectNotification = () => {
        if (autoRedirectCounter === null) return null;

        return (
            <RedirectNotification>
                <RedirectText>
                    <span>پخش زنده آغاز شد! انتقال به پخش در</span>
                    <RedirectTimerBadge>{autoRedirectCounter}</RedirectTimerBadge>
                    <span>ثانیه...</span>
                </RedirectText>
                <CancelButton
                    onEnterPress={() => setAutoRedirectCounter(null)}
                    onFocus={onRowFocus}
                />
            </RedirectNotification>
        );
    };

    const getMovieWatchButton = () => {
        if (timeRemaining !== null && timeRemaining > 0) {
            return getCountdownComponent();
        }

        const renderNormalPlayButton = () => {
            if (!isLogin) {
                if (content.isTicketing !== 1 || isPurchased === true) {
                    return (
                        <PlayButton
                            label={"ورود و تماشا"}
                            onEnterPress={handleWatchEvent}
                            onFocus={onRowFocus}
                            loading={canPlayLoading}
                            autofocus={true}
                        />
                    );
                } else {
                    return (
                        <PlayButton
                            label={"ورود و خرید بلیط"}
                            onEnterPress={handleWatchEvent}
                            onFocus={onRowFocus}
                            loading={canPlayLoading}
                            autofocus={true}
                        />
                    );
                }
            } else {
                if (content.isTicketing !== 1 || isPurchased === true) {
                    if (content.isFree === 1) {
                        return (
                            <PlayButton
                                label={"تماشا کن"}
                                onEnterPress={handleWatchEvent}
                                onFocus={onRowFocus}
                                loading={canPlayLoading}
                                autofocus={true}
                            />
                        );
                    } else if (content.isFree !== 1 && canPlay) {
                        return (
                            <PlayButton
                                label={"تماشا کن"}
                                onEnterPress={handleWatchEvent}
                                onFocus={onRowFocus}
                                loading={canPlayLoading}
                                autofocus={true}
                            />
                        );
                    } else {
                        return (
                            <PlayButton
                                label={"خرید اشتراک"}
                                onEnterPress={handleWatchEvent}
                                onFocus={onRowFocus}
                                loading={canPlayLoading}
                                autofocus={true}
                            />
                        );
                    }
                } else {
                    return (
                        <PlayButtonWithPopover
                            label={"خرید بلیط"}
                            onFocus={onRowFocus}
                            children={
                                <div className="relative">
                                    <p className="text-lg">
                                        جهت ادامه فرآیند خرید بلیط سینما آنلاین لطفا کد زیر را با تلفن
                                        همراه خود اسکن کنید و باقی مراحل را انجام دهید
                                    </p>
                                    <QRCode
                                        size={256}
                                        style={{
                                            height: "auto",
                                            maxWidth: "100%",
                                            width: "100%",
                                            padding: "15px",
                                        }}
                                        value={"https://manabakh.ir/movie/" + content.slug}
                                        viewBox={`0 0 256 256`}
                                        bgColor="#BFBFBF"
                                        fgColor="#121212"
                                    />
                                </div>
                            }
                        />
                    );
                }
            }
        };

        if (autoRedirectCounter !== null) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {renderNormalPlayButton()}
                    {renderRedirectNotification()}
                </div>
            );
        }

        return renderNormalPlayButton();
    };

    // ============================================================
    // Series-specific logic
    // ============================================================

    const getSeriesActionButton = () => {
        // Series don't show play button in hero - episodes have individual play buttons
        return null;
    };

    // ============================================================
    // Shared logic
    // ============================================================

    const getPreviewButton = () => {
        if (content?.trailer && content?.trailer !== null && content?.trailer !== "") {
            return (
                <PreviewButton
                    onEnterPress={() => navigate(`/trailer/${content.slug}`)}
                    onFocus={onRowFocus}
                />
            );
        }
        return null;
    };

    // ============================================================
    // Effects
    // ============================================================

    useEffect(() => {
        if (isMovie && content.isTicketing === 1) {
            getCinemaTicketData();
        }
    }, [content.slug, isMovie]);

    // Preload player data for faster playback (1-hour TTL cache)
    useEffect(() => {
        if (content?.slug) {
            preloadPlayerData(content.slug, contentKind);
        }
    }, [content?.slug, contentKind]);

    useEffect(() => {
        if (isMovie && isLogin) {
            getUserSubscriptionStatus().then((data) => {
                setCanPlay(data);
                setCanPlayLoading(false);
            });
        } else if (isMovie) {
            setCanPlayLoading(false);
        }
    }, [isLogin, isMovie]);

    // ============================================================
    // Render
    // ============================================================

    const RelatedContent = isMovie ? MovieRelatedMovies : SeriesRelatedMovies;

    return (
        <FocusContext.Provider value={focusKey}>
            <ScrollingRows ref={ref}>
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {/* Hero Scene */}
                    <DetailHeroScene
                        content={content}
                        onPlay={isMovie ? handleWatchEvent : undefined}
                        onTrailer={() => navigate(`/trailer/${content.slug}`)}
                        playLabel={isMovie ? getPlayLabel() : undefined}
                        isPlayLoading={isMovie ? canPlayLoading : false}
                        isSeries={isSeries}
                        onRowFocus={onRowFocus}
                        renderButtons={() => (
                            <>
                                {isMovie && getMovieWatchButton()}
                                {isSeries && getSeriesActionButton()}
                                {getPreviewButton()}
                                <div className="flex justify-end">
                                    <ContentAction contentKey={content.slug} onFocus={onRowFocus} isMultiEpisode={isSeries} />
                                </div>
                            </>
                        )}
                    />

                    {/* Series Episodes (only for series/program) */}
                    {isSeries && content?.contentType?.seriesInfo && (
                        <SeriesEpisodes
                            seasons={content?.contentType?.seriesInfo?.sort(
                                (a, b) => a.episodeNumber - b.episodeNumber
                            )}
                            slug={content?.slug}
                            onFocus={onRowFocus}
                            movie={content}
                        />
                    )}

                    {/* Artist Slider */}
                    {content?.artists && content.artists.length > 0 && (
                        <ArtistSlider
                            artists={content.artists}
                            onFocus={onRowFocus}
                            title="هنرمندان"
                        />
                    )}

                    {/* Related Content Section */}
                    <RelatedContentSection>
                        <RelatedContentTitle>
                            {`${getContentTypeName(content?.contentType?.kind)} های پیشنهادی`}
                        </RelatedContentTitle>
                    </RelatedContentSection>
                    <RelatedContent
                        slug={content?.slug}
                        onFocus={onRowFocus}
                        genreKeys={content.genreKeys}
                        categoryKeys={content.categoryKeys}
                        kind={content?.contentType?.kind}
                    />
                </Box>
            </ScrollingRows>
        </FocusContext.Provider>
    );
};

export default UnifiedDetailPage;
